<script setup lang="ts">
import { computed, ref } from "vue";
import VButton from "@magnit/core/src/components/VButton/VButton.vue";
import VCheckbox from "@magnit/core/src/components/VCheckbox/VCheckbox.vue";
import VListItem from "@magnit/core/src/components/VListItem/VListItem.vue";
import VSearch from "@magnit/core/src/containers/VSearch/VSearch.vue";
import VText from "@magnit/core/src/components/VText/VText.vue";
import CountClear from "./CountClear.vue";
import { isEqual, sortBy } from "lodash-es";
import type { IFilterValuesList, IGoodsGridFilter } from "../types";

const modelValue = defineModel<IFilterValuesList>("modelValue", {
  default: [],
});
const filter = defineModel<IGoodsGridFilter | null>("filter", {
  default: null,
});

const searchValue = ref("");
const selected = ref<IFilterValuesList>([...modelValue.value]);

const filtered = computed(() =>
  sortBy(
    filter.value?.elements.filter((item) =>
      item.name
        .toLowerCase()
        .trim()
        .includes(searchValue.value.toLowerCase().trim()),
    ),
    "name",
  ),
);

const isChanged = computed(() => !isEqual(selected.value, modelValue.value));

const onClearFilters = () => {
  selected.value = [];
};

const onSubmit = () => {
  modelValue.value = selected.value;
  filter.value = null;
};
</script>

<template>
  <div class="filter-grid-full">
    <CountClear :count="selected.length" @reset="onClearFilters" />

    <div class="filter-grid-full__search">
      <VSearch
        v-model="searchValue"
        :submit-visible="false"
        placeholder="Поиск значений"
      />
    </div>

    <div class="filter-grid-full__content">
      <label
        v-for="element in filtered"
        :key="element.name"
        :for="element.name"
        class="filter-grid-full__item"
      >
        <VListItem :divider="false" valign="center">
          <VText>{{ element.name }}</VText>

          <template #right>
            <VCheckbox
              :id="element.name"
              v-model="selected"
              :value="element.name"
              wrapper-class="filter-grid-full__item__input"
            />
          </template>
        </VListItem>
      </label>
      <VText
        v-if="filter?.elements?.length && !filtered?.length"
        tag="div"
        font="body-large-regular"
        color="secondary"
        class="filter-grid-full__empty"
      >
        Ничего не нашлось
      </VText>
    </div>
    <Teleport to="#teleport-unit-catalog-filters-modal-footer">
      <VButton
        theme="primary"
        :disabled="!isChanged"
        size="l"
        full-width
        @click="onSubmit"
      >
        Применить
      </VButton>
    </Teleport>
  </div>
</template>

<style scoped lang="postcss">
.filter-grid-full {
  flex-grow: 1;
  display: flex;
  flex-direction: column;

  &__search {
    padding-top: var(--pl-unit-x2);
    background-color: white;
    position: sticky;
    top: 0;
    z-index: var(--pl-z-index-level2);
  }

  &__content {
    padding: var(--pl-unit-x2) 0;
    display: flex;
    flex-direction: column;
    flex-grow: 1;
  }

  &__item {
    position: relative;
    z-index: var(--pl-z-index-level1);
    overflow: hidden;

    &__input {
      padding: var(--pl-unit-x2) 0;
    }
  }

  &__empty {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-grow: 1;
  }
}
</style>
